import React, { useEffect } from 'react';
import { Save } from 'lucide-react';
import SelectField from '../../../Forms/Input/SelectField';
import InputField from '../../../Forms/Input/InputField';
import ActionButton from '../../../Buttons/ActionButton';
import { useBranch } from '../../../../hooks/branches/useBranch';
import { useAuthUser } from '../../../../hooks/auth/useAuthUser';
import { useInventoryAdjustment } from '../../../../hooks/inventory/useInventoryAdjustment';

interface InventoryAdjustmentHeaderProps {
  branchId: number | null;
  reason: string;

  /** 🔹 Handlers (opcionales para modo lectura) */
  onChangeBranch?: (value: any) => void;
  onChangeReason?: (value: any) => void;
  onUpdateHeader?: () => void;

  /** 🆕 Modo solo lectura */
  readOnly?: boolean;
}

const InventoryAdjustmentHeader: React.FC<InventoryAdjustmentHeaderProps> = ({
  branchId,
  reason,
  onChangeBranch,
  onChangeReason,
  onUpdateHeader,
  readOnly = false,
}) => {
  const { branchDropdown, fetchBranchDropdown, myBranch, fetchMyBranch } = useBranch();
  const { hasRole } = useAuthUser();
  const { form, formErrors } = useInventoryAdjustment();

  // Solo estos roles operan/filtran sobre una sucursal distinta a la propia;
  // para el resto la sucursal se toma siempre de la del usuario, sin
  // mostrar el selector (mismo patrón que CashRegisterForm).
  const canPickBranch = hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  useEffect(() => {
    fetchMyBranch();
    if (canPickBranch) fetchBranchDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Precarga la sucursal del usuario por defecto (editable si tiene el rol).
  useEffect(() => {
    if (myBranch && !branchId && !readOnly) {
      onChangeBranch?.(myBranch.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myBranch]);

  return (
    <div className="mb-6 space-y-4 px-4">
      {canPickBranch && (
        <SelectField
          label="Sucursal"
          name="branchId"
          value={branchId ? String(branchId) : ''}
          options={branchDropdown.map((b) => ({
            label: b.name,
            value: String(b.id),
          }))}
          onChange={
            readOnly
              ? undefined
              : (e) => onChangeBranch?.(Number(e.target.value))
          }
          placeholder="Seleccione sucursal"
          iconKey="branch"
          required
          disabled={readOnly}
          error={!readOnly ? formErrors.branchId : undefined}
        />
      )}

      <InputField
        label="Motivo del Ajuste"
        name="reason"
        value={reason}
        onChange={
          readOnly
            ? undefined
            : (e) => onChangeReason?.(e.target.value)
        }
        placeholder="Ej. Ajuste por conteo físico"
        type="text"
        iconKey="edit"
        required
        disabled={readOnly}
        error={!readOnly ? formErrors.reason : undefined}
      />

      {/* 🔹 Botón solo en modo edición */}
      {!readOnly && form.id && (
        <ActionButton
          label="Actualizar encabezado"
          icon={<Save className="h-4 w-4" />}
          size="sm"
          fullWidth={false}
          onClick={onUpdateHeader}
        />
      )}
    </div>
  );
};

export default InventoryAdjustmentHeader;
